<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import {
  Activity,
  BookHeart,
  CalendarClock,
  HeartPulse,
  Pill,
  Plus,
  Ruler,
  Search,
  ShieldAlert,
  Sparkles,
  X,
} from 'lucide-vue-next'
import {
  CLINIC_CRF_MASKS,
  type ClinicCrfMask,
  type ClinicCrfMaskApplicability,
  type ClinicCrfMaskCategory,
} from '@clinic-crf/contract'

const emit = defineEmits<{ close: []; insert: [mask: ClinicCrfMask] }>()
const query = ref('')
const category = ref<'all' | ClinicCrfMaskCategory>('all')
const searchInput = ref<HTMLInputElement | null>(null)

const categoryLabels: Record<ClinicCrfMaskCategory, string> = {
  visit: 'Wizyta',
  vitals: 'Parametry życiowe',
  measurements: 'Pomiary',
  history: 'Wywiad',
  treatment: 'Leczenie',
  safety: 'Bezpieczeństwo',
}

const applicabilityLabels: Record<ClinicCrfMaskApplicability, string> = {
  core: 'Rdzeń wizyty',
  common: 'Często używana',
  protocol: 'Gdy wymaga protokół',
}

const categories = computed(() => (
  [...new Set(CLINIC_CRF_MASKS.map((mask) => mask.category))]
))

const filteredMasks = computed(() => {
  const needle = query.value.trim().toLocaleLowerCase('pl')
  return CLINIC_CRF_MASKS.filter((mask) => {
    if (category.value !== 'all' && mask.category !== category.value) return false
    if (!needle) return true
    return [mask.title, mask.description, ...mask.standards]
      .join(' ')
      .toLocaleLowerCase('pl')
      .includes(needle)
  })
})

function iconFor(maskCategory: ClinicCrfMaskCategory) {
  return {
    visit: CalendarClock,
    vitals: HeartPulse,
    measurements: Ruler,
    history: BookHeart,
    treatment: Pill,
    safety: ShieldAlert,
  }[maskCategory] ?? Activity
}

onMounted(() => searchInput.value?.focus())
</script>

<template>
  <Teleport to="body">
    <div class="clinical-mask-overlay" @mousedown.self="emit('close')">
      <section class="clinical-mask-dialog" role="dialog" aria-modal="true" aria-labelledby="clinical-mask-title" @keydown.esc="emit('close')">
        <header class="clinical-mask-header">
          <div class="clinical-mask-heading">
            <span class="clinical-mask-mark"><Sparkles :size="22" /></span>
            <div>
              <span class="eyebrow">Biblioteka kliniczna</span>
              <h2 id="clinical-mask-title">Dodaj maskę pytań</h2>
              <p>Wstaw gotowy, edytowalny blok do bieżącego formularza.</p>
            </div>
          </div>
          <button class="icon-button" type="button" aria-label="Zamknij bibliotekę masek" @click="emit('close')"><X :size="20" /></button>
        </header>

        <div class="clinical-mask-toolbar">
          <label class="clinical-mask-search">
            <Search :size="18" />
            <input ref="searchInput" v-model="query" type="search" placeholder="Szukaj: ciśnienie, saturacja, leki…" />
          </label>
          <nav class="clinical-mask-filters" aria-label="Kategorie masek">
            <button :class="{ active: category === 'all' }" type="button" @click="category = 'all'">Wszystkie</button>
            <button v-for="item in categories" :key="item" :class="{ active: category === item }" type="button" @click="category = item">{{ categoryLabels[item] }}</button>
          </nav>
        </div>

        <div class="clinical-mask-results" aria-live="polite">
          <article v-for="mask in filteredMasks" :key="mask.id" class="clinical-mask-card">
            <header>
              <span :class="['clinical-mask-icon', `category-${mask.category}`]"><component :is="iconFor(mask.category)" :size="21" /></span>
              <span :class="['clinical-mask-applicability', mask.applicability]">{{ applicabilityLabels[mask.applicability] }}</span>
            </header>
            <div class="clinical-mask-copy">
              <h3>{{ mask.title }}</h3>
              <p>{{ mask.description }}</p>
            </div>
            <div class="clinical-mask-standards">
              <span v-for="standard in mask.standards" :key="standard">{{ standard }}</span>
            </div>
            <footer>
              <small>{{ mask.fields.length }} pól<span v-if="mask.rules.length"> · {{ mask.rules.length }} reguła</span></small>
              <button class="button button-primary" type="button" @click="emit('insert', mask)"><Plus :size="16" /> Dodaj do formularza</button>
            </footer>
          </article>

          <div v-if="!filteredMasks.length" class="clinical-mask-empty">
            <Search :size="30" />
            <strong>Nie znaleziono maski</strong>
            <p>Zmień wyszukiwaną frazę albo wybierz inną kategorię.</p>
          </div>
        </div>

        <footer class="clinical-mask-disclaimer">
          <ShieldAlert :size="18" />
          <p><strong>Punkt startowy, nie gotowy protokół.</strong> Po dodaniu zweryfikuj pola, jednostki, wymagania, zakresy i logikę z protokołem, populacją oraz lekarzem/data managerem. Zakresy liczbowe są zabezpieczeniami technicznymi, nie normami klinicznymi.</p>
        </footer>
      </section>
    </div>
  </Teleport>
</template>
